import { useState, useMemo, type CSSProperties, type ReactNode } from 'react';
import {
  Plus, Home, MessageSquare, Briefcase, ClipboardList, Compass, Target,
  FileText, Users, BarChart3, FolderOpen, Plug, Settings2, Zap, ChevronRight,
  ChevronDown, Pin, Bell, BookOpen,
  Megaphone,
} from 'lucide-react';
import { cn } from '@/components/ui/utils';
import type { BrandListItem } from '../types/cowork';
import type { PlanInfo } from '../api/coworkApi';

/**
 * Feature scope. 'core' shows only what Beatly's campaign process needs;
 * 'full' restores the marketing-OS extras (projects, wiki, OKRs, diary,
 * dashboards, library). Pages and their APIs are untouched — this controls
 * navigation only, so nothing is lost by scoping down.
 */
const FEATURE_SCOPE = (import.meta.env.VITE_FEATURE_SCOPE as string) || 'core';
const showExtras = FEATURE_SCOPE === 'full';

export type SidebarView =
  | 'home' | 'chats' | 'inbox' | 'projects' | 'project-detail' | 'tasks'
  | 'brand-os' | 'brand-wiki' | 'okrs' | 'growth-diary'
  | 'agents' | 'agent-detail' | 'agent-chat'
  | 'dashboards' | 'library' | 'integrations'
  | 'settings' | 'usage' | 'campaigns'
  // Keep legacy values reachable by deep-link, but they're not in the nav
  | 'marketplace' | 'tools' | 'files' | 'approvals' | 'google-ads'
  | 'session' | 'artifact' | 'call' | 'onboarding';

export interface PinnedProject {
  id: string;
  title: string;
  emoji?: string;
}

interface OmniSidebarProps {
  currentView: SidebarView;
  onNavigate: (view: SidebarView) => void;
  onNewTask: () => void;
  onProjectOpen: (id: string) => void;
  onSearch?: () => void;
  pinnedProjects: PinnedProject[];
  inboxUnread?: number;
  // Brand picker
  availableBrands: BrandListItem[];
  activeBrandId?: string;
  onBrandChange: (id: string) => void;
  // Plan / credits
  planInfo?: PlanInfo | null;
  onUpgradeClick?: () => void;
  // Channels (integrations) live status
  connectedIntegrations?: Array<{ provider: string; label: string }>;
}

export function OmniSidebar(props: OmniSidebarProps) {
  const {
    currentView, onNavigate, onNewTask, pinnedProjects,
    availableBrands, activeBrandId, onBrandChange, planInfo, onUpgradeClick,
    connectedIntegrations = [], onProjectOpen,
  } = props;

  const [projectsOpen, setProjectsOpen] = useState(true);
  const [integrationsOpen, setIntegrationsOpen] = useState(false);

  const credits = planInfo?.creditsRemaining;

  return (
    <div className="w-[244px] shrink-0 bg-white flex flex-col h-full">
      {/* Logo */}
      <div className="px-4 pt-4 pb-3">
        <div className="text-[15px] tracking-tight" style={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
          omni<span className="text-[#be43ff]">:</span>ad
        </div>
      </div>

      {/* Brand switcher */}
      {availableBrands.length > 1 && (
        <div className="px-3 pb-2">
          <select
            value={activeBrandId || ''}
            onChange={(e) => onBrandChange(e.target.value)}
            className="w-full px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-[12px] text-[#344054] focus:outline-none focus:ring-2 focus:ring-[#be43ff]/20 focus:border-[#be43ff]/40 cursor-pointer appearance-none"
            style={{
              fontWeight: 500,
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23667085' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center',
            }}
          >
            {availableBrands.map((b) => (
              <option key={b.brandId} value={b.brandId}>{b.brandName || b.brandId}</option>
            ))}
          </select>
        </div>
      )}

      {/* + New task */}
      <div className="px-3 pb-3">
        <button
          onClick={onNewTask}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#be43ff] text-white text-[13px] hover:bg-[#a835e0] transition-colors"
          style={{ fontWeight: 600 }}
        >
          <Plus className="w-4 h-4" />
          New task
        </button>
      </div>

      {/* Top unlabeled cluster */}
      <div className="px-2 pb-2 flex flex-col gap-0.5">
        <NavItem
          icon={<Home className="w-3.5 h-3.5" />}
          label="Home"
          onClick={() => onNavigate('home')}
          active={currentView === 'home'}
        />
        <NavItem
          icon={<MessageSquare className="w-3.5 h-3.5" />}
          label="Chats"
          onClick={() => onNavigate('chats')}
          active={currentView === 'chats' || currentView === 'agent-chat'}
        />
      </div>

      {/* Scrollable section list */}
      <div className="flex-1 overflow-y-auto min-h-0 px-2 pb-3 flex flex-col gap-3">
        <Section label="Work">
          {showExtras && (<>
          <ExpandableNavItem
            icon={<Briefcase className="w-3.5 h-3.5" />}
            label="Projects"
            open={projectsOpen}
            onToggle={() => setProjectsOpen((o) => !o)}
            onClick={() => onNavigate('projects')}
            active={currentView === 'projects' || currentView === 'project-detail'}
          />
          {projectsOpen && (
            <div className="pl-7 flex flex-col gap-0.5 mt-0.5 mb-1">
              {pinnedProjects.length === 0 ? (
                <div className="text-[11px] text-[#98a2b3] px-2 py-1.5">No pinned projects</div>
              ) : (
                pinnedProjects.slice(0, 5).map((p) => (
                  <button
                    key={p.id}
                    onClick={() => onProjectOpen(p.id)}
                    className="flex items-center gap-2 px-2 py-1 rounded-md text-[12px] text-[#475569] hover:bg-[#f8f9fa] hover:text-[#101828] transition-colors text-left truncate"
                  >
                    {p.emoji ? <span>{p.emoji}</span> : <Pin className="w-3 h-3 text-[#cbd5e1]" />}
                    <span className="truncate">{p.title}</span>
                  </button>
                ))
              )}
              <button
                onClick={() => onNavigate('projects')}
                className="flex items-center gap-2 px-2 py-1 rounded-md text-[12px] text-[#be43ff] hover:bg-[#f8f5ff] transition-colors"
                style={{ fontWeight: 500 }}
              >
                <Plus className="w-3 h-3" />
                New project
              </button>
            </div>
          )}
          </>)}
          <NavItem
            icon={<ClipboardList className="w-3.5 h-3.5" />}
            label="Tasks"
            onClick={() => onNavigate('tasks')}
            active={currentView === 'tasks'}
          />
          <NavItem
            icon={<Megaphone className="w-3.5 h-3.5" />}
            label="Campaigns"
            onClick={() => onNavigate('campaigns')}
            active={currentView === 'campaigns'}
          />
        </Section>

        <Section label="Strategy">
          <NavItem
            icon={<Compass className="w-3.5 h-3.5" />}
            label="Brand OS"
            onClick={() => onNavigate('brand-os')}
            active={currentView === 'brand-os'}
          />
          {showExtras && (
            <NavItem
            icon={<BookOpen className="w-3.5 h-3.5" />}
            label="Brand Wiki"
            onClick={() => onNavigate('brand-wiki')}
            active={currentView === 'brand-wiki'}
          />
          )}
          {showExtras && (
            <NavItem
            icon={<Target className="w-3.5 h-3.5" />}
            label="OKRs"
            onClick={() => onNavigate('okrs')}
            active={currentView === 'okrs'}
          />
          )}
          {showExtras && (
            <NavItem
            icon={<FileText className="w-3.5 h-3.5" />}
            label="Growth Diary"
            onClick={() => onNavigate('growth-diary')}
            active={currentView === 'growth-diary'}
          />
          )}
        </Section>

        <Section label="Team">
          <NavItem
            icon={<Users className="w-3.5 h-3.5" />}
            label="Agents"
            onClick={() => onNavigate('agents')}
            active={currentView === 'agents' || currentView === 'agent-detail' || currentView === 'marketplace'}
          />
        </Section>

        {showExtras && (
          <Section label="Insights">
          {showExtras && (
            <NavItem
            icon={<BarChart3 className="w-3.5 h-3.5" />}
            label="Dashboards"
            onClick={() => onNavigate('dashboards')}
            active={currentView === 'dashboards'}
          />
          )}
          {showExtras && (
            <NavItem
            icon={<FolderOpen className="w-3.5 h-3.5" />}
            label="Library"
            onClick={() => onNavigate('library')}
            active={currentView === 'library' || currentView === 'files'}
          />
          )}
        </Section>
        )}

        <Section label="Channels">
          <ExpandableNavItem
            icon={<Plug className="w-3.5 h-3.5" />}
            label="Integrations"
            open={integrationsOpen}
            onToggle={() => setIntegrationsOpen((o) => !o)}
            onClick={() => onNavigate('integrations')}
            active={currentView === 'integrations' || currentView === 'google-ads'}
          />
          {integrationsOpen && (
            <div className="pl-7 flex flex-col gap-0.5 mt-0.5 mb-1">
              {connectedIntegrations.length === 0 ? (
                <div className="text-[11px] text-[#98a2b3] px-2 py-1.5">No channels connected</div>
              ) : (
                connectedIntegrations.map((c) => (
                  <button
                    key={c.provider}
                    onClick={() => onNavigate('integrations')}
                    className="flex items-center gap-2 px-2 py-1 rounded-md text-[12px] text-[#475569] hover:bg-[#f8f9fa] hover:text-[#101828] transition-colors text-left truncate"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e]" />
                    <span className="truncate">{c.label}</span>
                  </button>
                ))
              )}
              <button
                onClick={() => onNavigate('integrations')}
                className="flex items-center gap-2 px-2 py-1 rounded-md text-[12px] text-[#be43ff] hover:bg-[#f8f5ff] transition-colors"
                style={{ fontWeight: 500 }}
              >
                <Plus className="w-3 h-3" />
                Connect more
              </button>
            </div>
          )}
        </Section>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-100 px-2 py-2 flex flex-col gap-0.5">
        <button
          onClick={onUpgradeClick}
          className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-md text-[12px] text-[#475569] hover:bg-[#f8f9fa] transition-colors w-full"
        >
          <span className="flex items-center gap-2">
            <Zap className="w-3.5 h-3.5 text-[#fbbf24]" />
            <span style={{ fontWeight: 500 }}>{typeof credits === 'number' ? `${credits.toLocaleString()} credits` : 'Credits'}</span>
          </span>
          <span className="text-[10px] text-[#be43ff]" style={{ fontWeight: 600 }}>Top up</span>
        </button>
        <NavItem
          icon={<Settings2 className="w-3.5 h-3.5" />}
          label="Settings"
          onClick={() => onNavigate('settings')}
          active={currentView === 'settings'}
        />
      </div>
    </div>
  );
}

// ─── Atoms ──────────────────────────────────────────────────────────────────

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <div
        className="px-3 pt-1 pb-1.5 text-[#94a3b8]"
        style={{
          fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
          textTransform: 'uppercase',
        } as CSSProperties}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function NavItem({
  icon, label, onClick, active, badge,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  active: boolean;
  badge?: number;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[13px] transition-colors w-full text-left',
        active
          ? 'bg-[#f1f5f9] text-[#0f172a]'
          : 'text-[#475569] hover:bg-[#f8f9fa] hover:text-[#101828]',
      )}
      style={{ fontWeight: active ? 600 : 500 }}
    >
      <span className={active ? 'text-[#be43ff]' : 'text-[#64748b]'}>{icon}</span>
      <span className="flex-1">{label}</span>
      {typeof badge === 'number' && badge > 0 && (
        <span
          className="rounded-full bg-[#be43ff] text-white px-1.5 py-0.5"
          style={{ fontSize: 10, fontWeight: 700, minWidth: 18, textAlign: 'center' }}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}

function ExpandableNavItem({
  icon, label, open, onToggle, onClick, active,
}: {
  icon: ReactNode;
  label: string;
  open: boolean;
  onToggle: () => void;
  onClick: () => void;
  active: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-1 pr-1 rounded-md transition-colors w-full',
        active ? 'bg-[#f1f5f9] text-[#0f172a]' : 'text-[#475569] hover:bg-[#f8f9fa] hover:text-[#101828]',
      )}
      style={{ fontWeight: active ? 600 : 500 }}
    >
      <button
        onClick={onClick}
        className="flex items-center gap-2.5 pl-3 py-1.5 text-[13px] flex-1 text-left"
      >
        <span className={active ? 'text-[#be43ff]' : 'text-[#64748b]'}>{icon}</span>
        <span>{label}</span>
      </button>
      <button
        onClick={onToggle}
        className="p-1 rounded hover:bg-[#e2e8f0] transition-colors"
        aria-label={open ? 'Collapse' : 'Expand'}
      >
        {open ? <ChevronDown className="w-3 h-3 text-[#64748b]" /> : <ChevronRight className="w-3 h-3 text-[#64748b]" />}
      </button>
    </div>
  );
}
